import type {
  AdminApiAiUsage,
  AdminApiAiUsageByFeature,
} from "@repo/app-types/entities/aiCreditUsage";
import {
  USAGE_BASED_FEATURE_UNIT_NOUNS,
  getAiFeatureDefinition,
} from "@repo/app-types/helpers/aiFeatureCatalog";
import { InfoBanner } from "@repo/ui/InfoBanner/infoBanner";
import { SimpleModalVariant } from "@repo/ui/Modal/modal";
import {
  SimpleSelect,
  type SimpleSelectOption,
} from "@repo/ui/Select/simpleSelect";
import { DefaultSkeleton, TableSkeleton } from "@repo/ui/Skeletons/skeletons";
import { Size } from "@repo/ui/helpers/helpers";
import { IconCalendar } from "@tabler/icons-react";
import { DateTime } from "luxon";
import { useState, type FC } from "react";
import { twMerge } from "tailwind-merge";
import { useAiUsageQuery } from "../../../api/entities/company.js";
import { SettingsTable } from "../shared/settingsTable.js";
import { SettingsTableBodySection } from "../shared/settingsTableBodySection.js";
import { SettingsTableDataCell } from "../shared/settingsTableDataCell.js";
import { SettingsTableHeaderCell } from "../shared/settingsTableHeaderCell.js";
import { SettingsTableHeaderSection } from "../shared/settingsTableHeaderSection.js";
import {
  formatPeriodLabel,
  formatPeriodResetDate,
  formatUsageAgainstIncluded,
  formatWholeNumber,
} from "./usageFormatting.js";

/**
 * The AI usage section of the redesigned billing page: period picker, usage
 * bar against the plan's included credits, and the per-feature breakdown.
 * Usage-only preview — nothing here bills, alerts, or pauses features.
 */
export const AiCreditsSection: FC = () => {
  const [periodOffset, setPeriodOffset] = useState(0);
  // The current-period query doubles as the section shell (period list), so
  // switching periods re-renders the body without blanking the picker.
  const { currentData: currentUsage } = useAiUsageQuery({ periodOffset: 0 });
  // `currentData` is intentionally argument-specific. RTK Query's `data`
  // falls back to the previous argument's result while a new period loads,
  // which would make stale usage look like it belongs to the selected period.
  const {
    currentData: selectedUsage,
    isError: isUsageError,
    isFetching,
  } = useAiUsageQuery({ periodOffset });
  const isUsageLoading =
    !isUsageError && (isFetching || selectedUsage === undefined);

  const periodOptions: readonly SimpleSelectOption<number>[] =
    currentUsage?.periods.map((period) => ({
      value: period.offset,
      label: formatPeriodLabel(period),
    })) ?? [];

  return (
    <section>
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-start">
        <h2 className="text-base font-semibold text-foreground">
          AI usage and credits
        </h2>
        {currentUsage ? (
          <SimpleSelect
            isLabelHidden
            label="Usage period"
            leftAccessory={<IconCalendar className="size-4" />}
            onChange={setPeriodOffset}
            options={periodOptions}
            size={Size.SMALL}
            value={periodOffset}
          />
        ) : null}
      </div>

      <div className="mt-5">
        <InfoBanner
          title="Usage preview"
          description="Usage is tracked, but nothing is billed or paused based on it."
        />
      </div>

      <div className="mt-5">
        {isUsageError ? (
          <div role="alert">
            <InfoBanner
              title="Unable to load AI usage"
              description="Refresh the page to try again."
              variant={SimpleModalVariant.Warning}
            />
          </div>
        ) : isUsageLoading ? (
          <div className="rounded-md border border-border-light bg-white px-5 py-4">
            <DefaultSkeleton />
          </div>
        ) : selectedUsage ? (
          <UsageOverview usage={selectedUsage} />
        ) : null}
      </div>
      {isUsageError ? null : (
        <div className="mt-4">
          <UsageBreakdown
            features={selectedUsage?.features ?? []}
            isLoading={isUsageLoading}
          />
        </div>
      )}
    </section>
  );
};

interface UsageOverviewProps {
  usage: AdminApiAiUsage;
}

const UsageOverview: FC<UsageOverviewProps> = (props) => {
  const { usage } = props;

  const isCurrentPeriod = usage.selected_period.offset === 0;
  const includedCredits = usage.included_ai_credits;
  const usagePercentage =
    includedCredits !== null && includedCredits > 0
      ? (usage.credits_used / includedCredits) * 100
      : null;

  const projectedCredits = isCurrentPeriod ? projectPeriodTotal(usage) : null;

  return (
    <section className="rounded-md border border-border-light bg-white px-5 py-4">
      <div className="flex items-center justify-between gap-4">
        <p className="text-sm font-semibold text-foreground">
          {includedCredits === null ? "AI credits used" : "Monthly AI credits"}
        </p>
        <p className="shrink-0 text-sm font-semibold text-foreground tabular-nums">
          {usagePercentage === null
            ? formatWholeNumber(usage.credits_used)
            : isCurrentPeriod
              ? `${Math.max(0, Math.round(100 - usagePercentage))}% left`
              : `${Math.round(usagePercentage)}% used`}
        </p>
      </div>

      {usagePercentage !== null ? (
        <UsageBar className="mt-3 w-full" percentage={usagePercentage} />
      ) : null}

      <div className="mt-2 flex flex-wrap items-center justify-between gap-x-4 gap-y-1 text-xs text-muted-foreground">
        <p>
          {formatUsageAgainstIncluded(
            usage.credits_used,
            includedCredits,
            "credits used",
          )}
          <span aria-hidden="true"> · </span>
          {isCurrentPeriod
            ? `Resets ${formatPeriodResetDate(usage.selected_period)}`
            : formatPeriodLabel(usage.selected_period)}
        </p>
        <p className="text-right">
          {isCurrentPeriod
            ? projectedCredits !== null
              ? `Projected: ${formatWholeNumber(projectedCredits)} by reset`
              : `${formatWholeNumber(usage.audio_minutes_used)} audio minutes used`
            : "Period ended"}
        </p>
      </div>
    </section>
  );
};

/**
 * Linear projection of the period's final credit total from the fraction of
 * the period elapsed. Null while so little has elapsed that the extrapolation
 * would be noise.
 */
function projectPeriodTotal(usage: AdminApiAiUsage): number | null {
  const start = DateTime.fromISO(usage.selected_period.starts_at).toMillis();
  const end = DateTime.fromISO(usage.selected_period.ends_at).toMillis();
  const elapsedFraction = (Date.now() - start) / (end - start);

  if (elapsedFraction < 0.03 || elapsedFraction > 1) {
    return null;
  }
  return usage.credits_used / elapsedFraction;
}

interface UsageBreakdownProps {
  features: readonly AdminApiAiUsageByFeature[];
  isLoading: boolean;
}

const UsageBreakdown: FC<UsageBreakdownProps> = (props) => {
  const { features: rows, isLoading } = props;

  return (
    <section className="space-y-4">
      <h2 className="text-base font-semibold text-foreground">
        Usage by feature
      </h2>
      <SettingsTable>
        <SettingsTableHeaderSection>
          <tr>
            <SettingsTableHeaderCell className="w-[45%]">
              Feature
            </SettingsTableHeaderCell>
            <SettingsTableHeaderCell className="w-[30%]">
              Volume
            </SettingsTableHeaderCell>
            <SettingsTableHeaderCell className="w-[25%] text-right">
              Credits used
            </SettingsTableHeaderCell>
          </tr>
        </SettingsTableHeaderSection>
        <SettingsTableBodySection>
          {isLoading ? <TableSkeleton /> : null}
          {rows.map((row) => (
            <tr className="hover:bg-gray-50" key={row.feature_key}>
              <SettingsTableDataCell>
                {getAiFeatureDefinition(row.feature_key)?.label ??
                  row.feature_key}
              </SettingsTableDataCell>
              <SettingsTableDataCell>
                {formatFeatureUnitCount(row)}
              </SettingsTableDataCell>
              <SettingsTableDataCell className="text-right tabular-nums">
                {formatWholeNumber(row.credits_used)}
              </SettingsTableDataCell>
            </tr>
          ))}
        </SettingsTableBodySection>
      </SettingsTable>
    </section>
  );
};

function formatFeatureUnitCount(feature: AdminApiAiUsageByFeature): string {
  const featureUnitNoun = USAGE_BASED_FEATURE_UNIT_NOUNS[feature.feature_key];
  const noun =
    feature.feature_unit_count === 1
      ? featureUnitNoun.singular
      : featureUnitNoun.plural;
  return `${formatWholeNumber(feature.feature_unit_count)} ${noun}`;
}

interface UsageBarProps {
  percentage: number;
  className?: string;
}

/** Credits consumed against the plan's allowance, warming as it fills. */
const UsageBar: FC<UsageBarProps> = (props) => {
  const { percentage, className } = props;

  const fillColor =
    percentage >= 100
      ? "bg-red-500"
      : percentage >= 80
        ? "bg-amber-500"
        : "bg-indigo-600";

  return (
    <div
      aria-label={`${Math.round(percentage)}% of included credits used`}
      className={twMerge(
        "h-2 overflow-hidden rounded-full bg-gray-100",
        className,
      )}
      role="meter"
      aria-valuemax={100}
      aria-valuemin={0}
      aria-valuenow={Math.min(100, Math.round(percentage))}
    >
      <div
        className={twMerge(
          "h-full rounded-full transition-all duration-300",
          fillColor,
        )}
        style={{ width: `${Math.min(100, percentage)}%` }}
      />
    </div>
  );
};
